import { useEffect, useState } from 'react';
import { twMerge } from 'tailwind-merge';
import { useParams, useNavigate } from 'react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import UpButton from '../../../components/UpButton';
import Skeleton from '../../../components/Skeleton';
import UpRadioGroup from '../../../components/UpRadioGroup';
import BottomFooter from '../../../components/BottomFooter';
import { ApiConstants } from '../../../constants/ApiConstants';
import {
  MenuIcon,
  PlusIcon,
  ShopIcon,
  MotorCycleIcon,
} from '../../../assets/Icons';
import type { Customer, OrderOrder } from '../../../types/codexOrder';
import {
  deleteCustomerAddresses,
  getCustomerAddresses,
  updateOrder,
} from '../../../services/api';
import type { CustomerAddress } from '../../../types/customerAddress';
import BottomModal from '../../../components/BottomModal';
import { toast } from '../../../components/UpToast';
import UpPopover from '../../../components/UpPopover';

function OrderTypeModal({
  order,
  isOpen,
  onClose,
  customer,
  newAddressId,
  awaitingCheckout,
}: {
  isOpen: boolean;
  onClose: () => void;
  awaitingCheckout: boolean;
  newAddressId: string | null;
  order: OrderOrder | undefined;
  customer: Customer | undefined;
}) {
  if (!awaitingCheckout || !order) {
    return null;
  }

  return (
    <BottomModal isOpen={isOpen} onClose={onClose}>
      <SelectOrderMode
        order={order}
        onClose={onClose}
        customer={customer}
        newAddressId={newAddressId}
      />
    </BottomModal>
  );
}

function SelectOrderMode({
  order,
  onClose,
  customer,
  newAddressId,
}: {
  order: OrderOrder;
  onClose: () => void;
  newAddressId: string | null;
  customer: Customer | undefined;
}) {
  const params = useParams();
  const navigate = useNavigate();
  const orderId = params.orderId;
  const queryClient = useQueryClient();
  const [modeOrAddressUpdated, setModeOrAddressUpdated] = useState(false);
  const [selectedMode, setSelectedMode] = useState(order?.details?.order_type);
  const [selectedDeliveryAddress, setSelectedDeliveryAddress] = useState(
    String(customer?.address?.id ?? ''),
  );

  useEffect(() => {
    if (newAddressId) {
      setSelectedDeliveryAddress(newAddressId);
      setSelectedMode(ApiConstants.TYPE_DELIVERY);
      setModeOrAddressUpdated(true);
    }
  }, [newAddressId]);

  const {
    data: customerAddresses,
    isLoading: addressLoading,
    refetch: refetchAddresses,
  } = useQuery({
    retry: false,
    refetchOnMount: true,
    enabled: !!(customer?.phone && orderId),
    refetchOnWindowFocus: false,
    queryFn: async () => {
      if (!customer?.phone || !orderId) {
        return Promise.reject(
          new Error('Customer phone number and order are required.'),
        );
      }

      const response = await getCustomerAddresses(order?.details?.biz_id, {
        customer_phone: customer?.phone,
        reference_id: orderId,
        include_delivery_details: true,
        location_id: String(order?.store?.id),
      });
      const addresses = response?.data || [];

      if (addresses?.length > 0 && newAddressId) {
        return moveSelectedAddressToFirst(addresses, newAddressId);
      }

      return addresses;
    },
    queryKey: [
      'getCustomerAddresses',
      orderId,
      customer?.phone,
      order?.details?.biz_id,
      order?.store?.id,
      newAddressId,
    ],
  });

  const { mutate: callUpdateOrder, isPending } = useMutation({
    mutationFn: (params: unknown) => updateOrder(params),
    onSuccess: resp => {
      queryClient.setQueryData(['getOrderDetails', orderId], resp);
      toast.info('Order total recalculated!');
      onClose();
    },
    onError: err => {
      const error = err as { response?: { data?: { detail?: string } } };
      toast.info(
        error?.response?.data?.detail ??
          'Something went wrong. Order update action failed.',
      );
    },
  });

  const { mutate: callDeleteAddress, isPending: deleteAddressLoading } =
    useMutation({
      mutationFn: ({
        bizId,
        addressId,
        params,
      }: {
        bizId: string;
        addressId: string;
        params: { customer_phone: string; reference_id: string };
      }) => deleteCustomerAddresses(bizId, addressId, params),
      onSuccess: () => {
        void refetchAddresses();
      },
      onError: err => {
        const error = err as { response?: { data?: { detail?: string } } };
        toast.info(
          error?.response?.data?.detail ??
            'Something went wrong. Address delete action failed.',
        );
      },
    });

  function moveSelectedAddressToFirst(
    addresses: CustomerAddress[],
    firstId: string | null,
  ) {
    const selectedIndex = addresses.findIndex(
      address => String(address.id) === String(firstId),
    );

    if (selectedIndex === -1) {
      return addresses;
    }

    return [
      addresses[selectedIndex],
      ...addresses.slice(0, selectedIndex),
      ...addresses.slice(selectedIndex + 1),
    ];
  }

  function onContinueClick() {
    const params = {
      reference_id: orderId,
      order_type: selectedMode,
    } as {
      order_type: string;
      reference_id: string;
      address?: CustomerAddress;
      address_id?: string;
    };

    if (selectedMode === ApiConstants.TYPE_DELIVERY) {
      params.address_id = selectedDeliveryAddress;
      params.address = customerAddresses?.find(
        add => String(add.id) === String(selectedDeliveryAddress),
      );
    }

    callUpdateOrder(params);
  }

  function handleAddNewAddress() {
    void navigate(`/${orderId ?? ''}/new-address`);
  }

  function handleAddressDelete(id: number) {
    if (customer?.phone && orderId) {
      callDeleteAddress({
        bizId: order?.details?.biz_id,
        addressId: String(id),
        params: { customer_phone: customer?.phone, reference_id: orderId },
      });
    }
  }

  if (addressLoading || deleteAddressLoading) {
    return <SelectOrderModeSkeleton />;
  }

  return (
    <div className="flex flex-col gap-y-5 pb-24">
      <div className="text-lg">Select order mode</div>

      <div className="rounded-lg p-4 flex items-center gap-x-2 bg-white">
        <div
          className={twMerge(
            'p-3 flex justify-center items-center flex-1 bg-up-black-5 text-up-black-50 rounded-3xl gap-x-2',
            selectedMode === ApiConstants.TYPE_PICKUP &&
              'bg-brand-background text-brand-text',
          )}
          onClick={() => {
            setModeOrAddressUpdated(true);
            setSelectedDeliveryAddress('');
            setSelectedMode(ApiConstants.TYPE_PICKUP);
          }}>
          <ShopIcon
            className={twMerge(
              'text-up-black-50',
              selectedMode === ApiConstants.TYPE_PICKUP && 'text-brand-text',
            )}
          />
          <span>Pickup</span>
        </div>
        <div
          className={twMerge(
            'p-3 flex justify-center items-center flex-1 bg-up-black-5 text-up-black-50 rounded-3xl gap-x-2',
            selectedMode === ApiConstants.TYPE_DELIVERY &&
              'bg-brand-background text-brand-text',
          )}
          onClick={() => {
            setModeOrAddressUpdated(false);
            setSelectedDeliveryAddress('');
            setSelectedMode(ApiConstants.TYPE_DELIVERY);
          }}>
          <MotorCycleIcon
            className={twMerge(
              'text-up-black-50',
              selectedMode === ApiConstants.TYPE_DELIVERY && 'text-brand-text',
            )}
          />
          <span>Delivery</span>
        </div>
      </div>

      {selectedMode === ApiConstants.TYPE_PICKUP && (
        <div className="rounded-lg p-4 pb-2 bg-white">
          <div className="flex items-center gap-x-2">
            <ShopIcon className="text-brand-icon" />
            <p className="text-lg">Pickup from</p>
          </div>
          <UpRadioGroup
            value={selectedMode}
            name={ApiConstants.TYPE_PICKUP}
            onChange={() => {
              setModeOrAddressUpdated(true);
              setSelectedDeliveryAddress('');
              setSelectedMode(ApiConstants.TYPE_PICKUP);
            }}
            options={[
              {
                id: ApiConstants.TYPE_PICKUP,
                value: ApiConstants.TYPE_PICKUP,
                description: order?.store?.address,
                label: `${order?.details?.brand?.name ? `${order?.details?.brand?.name} - ` : ''}${order?.store?.name || ''}`,
              },
            ]}
          />
        </div>
      )}

      {selectedMode === ApiConstants.TYPE_DELIVERY && (
        <div className="rounded-lg p-4 bg-white">
          <div className="flex items-center gap-x-2">
            <MotorCycleIcon className="text-brand-icon" />
            <p className="text-lg">Deliver to</p>
          </div>
          <div
            className={twMerge(
              'w-full pt-5 mb-1',
              customerAddresses?.length && 'border-b border-up-black-20 pb-5',
            )}>
            <p
              onClick={handleAddNewAddress}
              className="text-brand-icon text-sm cursor-pointer flex items-center max-w-max">
              <PlusIcon className="mr-1" />
              <span>Add new address</span>
            </p>
          </div>
          <UpRadioGroup
            value={selectedDeliveryAddress}
            name={ApiConstants.TYPE_DELIVERY}
            onChange={v => {
              setSelectedDeliveryAddress(v);
              setModeOrAddressUpdated(true);
            }}
            options={(customerAddresses ?? [])?.map(add => ({
              id: String(add.id),
              value: String(add.id),
              description: [add.address_1, add.sub_locality, add.city, add.pin]
                .filter(Boolean)
                .join(', '),
              label: (add?.tag ?? '').split('__')[0],
              disabled: !add.can_deliver_at_location,
              additionalAction: (
                <UpPopover
                  arrowAlign="end"
                  content={
                    <div className="py-1.5">
                      <div
                        className="px-3 py-1.5"
                        onClick={() => {
                          handleAddressDelete(add.id);
                        }}>
                        Delete
                      </div>
                    </div>
                  }>
                  <MenuIcon className="text-up-black-40" />
                </UpPopover>
              ),
            }))}
          />
        </div>
      )}

      {modeOrAddressUpdated && (
        <BottomFooter>
          <UpButton onClick={onContinueClick} loading={isPending}>
            Continue
          </UpButton>
        </BottomFooter>
      )}
    </div>
  );
}

function SelectOrderModeSkeleton() {
  return (
    <div className="px-2 py-4 overflow-x-hidden space-y-4 mt-5">
      <Skeleton rows={1} />
      <br />
      <Skeleton rows={3} />
    </div>
  );
}

export default OrderTypeModal;
